import { Button } from "@mui/material";
import { Box, Stack } from "@mui/system";
import GenericTiptapEditor from "../GenericTipTapEditor/GenericTiptapEditor";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import {
  hasMeaningfulContent,
  parseCommentToPlainString,
} from "./discussionUtils/discussionHelpers";
import "./CommentEditor.css";
import {
  setIsCommentLoading,
  setSelectedMentionsUsers,
} from "../../features/genericCommentsAndFilesSlice";
import { useDispatch } from "react-redux";

const CommentBox = ({
  addComment,
  handleUpdateComment,
  commentId,
  defaultValue = "", // default value when editing a comment
  onCancel,
  onSave,
  onAddSuccess, //called only when new comment is added
  initialContent = "",
}: {
  addComment?: (commentBody: any) => Promise<void>;
  commentId?: string;
  defaultValue?: string; // default value when editing a comment
  handleUpdateComment?: (commentBody: any) => Promise<void>;
  onCancel?: () => void;
  onSave?: (updatedValue: string) => void;
  onAddSuccess?: () => void; // this for collapse the comment box
  initialContent?: any;
}) => {
  const dispatch = useDispatch();
  const [commentValue, setCommentValue] = useState<any>(
    defaultValue || initialContent || ""
  );
  const [originalValue, setOriginalValue] = useState<string>(() =>
    parseCommentToPlainString(defaultValue || "")
  );
  //const [isCommentLoading, setIsCommentLoading] = useState(false);

  const {
    objectType,
    selectedMentionsUsers,
    referenceModule,
    commentsLoader,
    postCommentTrackID,
    isCommentLoading,
  } = useSelector((state: any) => state.genericCommentsAndFiles);
  const location = useLocation();

  const currentUrl = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;

  function extractImageFilenamesFromHTML(html: string): string[] {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    const imageElements = temp.querySelectorAll("img");
    const filenames: string[] = [];

    imageElements.forEach((img) => {
      const filename =
        img.getAttribute("data-filename") ||
        img.getAttribute("src")?.split("/").pop(); // fallback
      if (filename) filenames.push(filename);
    });

    return filenames;
  }

  // Function to reset the editor (delete all files and clear content and mentions)
  const handleCancel = async () => {
    const imageFilenames = extractImageFilenamesFromHTML(commentValue);
    if (imageFilenames.length > 0) {
      //delete ep call
      // const deletePromises = imageFilenames.map((filename) =>
      //   handlers.deleteImage(oauthTokenHeader, filename)
      // );
      // try {
      //   await Promise.all(deletePromises);
      // } catch (err) {
      //   console.error("Failed to delete one or more images", err);
      // }
    }

    setCommentValue(defaultValue || ""); // reset the editor
    onCancel?.(); // call onCancel if provided (when edit the comment)
  };

  //-- Function to handle adding the new comments
  const handleSaveComments = async () => {
    if (
      !hasMeaningfulContent(commentValue) ||
      !objectType ||
      parseCommentToPlainString(commentValue) === originalValue
    ) {
      return;
    }
    dispatch(setIsCommentLoading(true));
    const commentBody = {
      object_type: objectType, //According to the implemented entity
      comment: parseCommentToPlainString(commentValue), // parse the comment to plain text
      users: selectedMentionsUsers ? selectedMentionsUsers : [], //According to all the users which have been tagged in the comment
      reference_module: referenceModule,
      reference_url: currentUrl, // Link to the specific screen, where users can see the comment
    };

    try {
      // this for edit comment when we get comment ID
      if (commentId) {
        const editCommentBody = {
          comment_id: commentId,
          comment: parseCommentToPlainString(commentValue),
          users: selectedMentionsUsers ? selectedMentionsUsers : [],
          reference_module: referenceModule,
          reference_url: currentUrl,
        } as any;
        await handleUpdateComment?.(editCommentBody); // update the comment
        onSave?.(commentValue); // optional: to inform parent (e.g. MessageItem)
        return;
      } else {
        await addComment?.(commentBody); // add new comment
        onAddSuccess?.(); // collapse the comment box
      }
    } finally {
      setCommentValue(""); // reset the editor
      dispatch(setIsCommentLoading(false));
      dispatch(setSelectedMentionsUsers([]));
    }
  };

  useEffect(() => {
    if (initialContent) {
      setCommentValue(initialContent);
    } else {
      setCommentValue(defaultValue || "");
      setOriginalValue(parseCommentToPlainString(defaultValue || ""));
    }
  }, [initialContent, defaultValue]);

  return (
    <Box sx={{ width: "96%" }}>
      <Stack direction="column" gap={2}>
        <Stack direction="column" gap={2}>
          <GenericTiptapEditor
            content={commentValue ?? commentValue}
            onUpdate={(updatedContent: string) => {
              if (updatedContent) {
                setCommentValue(updatedContent);
              } else {
                setCommentValue("");
              }
            }}
            placeholder="Type @ to mention and notify someone."
            editable={!commentsLoader} // Disable editing when commentsLoader is true
            style={{
              opacity: commentsLoader ? 0.5 : 1,
              pointerEvents: commentsLoader ? "none" : "auto",
            }}
            autoFocus
          />
          <Stack direction="row" gap={1}>
            <Button
              data-tracker={postCommentTrackID}
              variant="contained"
              color="secondary"
              loading={isCommentLoading}
              loadingPosition="end"
              endIcon={isCommentLoading && <SaveIcon />}
              onClick={handleSaveComments}
              disabled={!hasMeaningfulContent(commentValue) || isCommentLoading}
            >
              {/* {commentId ? "Save" : "LABEL"} */}
              Save
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCancel}
              disabled={isCommentLoading}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CommentBox;
